import React, { useState, useMemo, useEffect } from 'react';
import { get, round } from 'lodash';
import Button from '@tds/core-button';
import { Formik, Form, Field } from 'formik';
import { Delete, Edit, IconButton } from '@tds/core-interactive-icon';
import PropTypes from 'prop-types';

import { data } from './data';
import Modal from '../../../design/shared/modal';

import {
  customStyles,
  ModalFieldLabel,
  ModalFooter,
  Table,
  Tr,
  Td,
  Th,
  TdImp,
  TdLabel
} from './css';
import { formatCurrency } from '../../../utils/currencyFormatter';

// Function to get distinct categories from the data
const getDistinctCategories = list => {
  return [...new Set(list.map(item => item.Category))];
};

// Function to get distinct item description line 2 for a given category
const getDistinctItemDescriptionLine2 = (list, category) => {
  const filteredData = list.filter(item => item.Category === category);
  return [
    ...new Set(filteredData.map(item => item['Item Description Line 2']))
  ];
};

const FmMicrosoftLicensing = ({ onChange, defaultValues, orderData }) => {
  const [isModalVisible, setModalVisible] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [
    microsoftlicensingSelections,
    setMicrosoftlicensingSelections
  ] = useState([]);
  const [errors, setErrors] = useState();

  useEffect(() => {
    setMicrosoftlicensingSelections(get(defaultValues, 'selections', []));
  }, [orderData]);

  const categories = getDistinctCategories(data);

  const totals = useMemo(() => {
    let totalCostSum = 0;
    let totalPriceSum = 0;
    microsoftlicensingSelections.forEach(item => {
      totalCostSum += round(item.totalCost, 2);
      totalPriceSum += round(item.totalPrice, 2);
    });
    return {
      totalCost: totalCostSum,
      totalPrice: totalPriceSum
    };
  }, [microsoftlicensingSelections]);

  const adjustedTotalPrice = useMemo(() => totals.totalPrice, [totals]);
  const adjustedTotalCost = useMemo(() => totals.totalCost, [totals]);

  const showModal = () => {
    setModalData(null);
    setModalVisible(true);
  };

  const onCloseModal = () => {
    setModalVisible(false);
  };

  const addLicencingRow = row => {
    if (row.qtyOfLicenses <= 0) {
      setErrors(prevErrors => ({
        ...prevErrors,
        qtyOfLicenses: 'Quantity of Licenses must be greater than 0'
      }));
      return;
    }
    setMicrosoftlicensingSelections([...microsoftlicensingSelections, row]);
  };

  const editItem = (e, row) => {
    e.preventDefault();
    setModalData(row);
    setModalVisible(true);
  };

  const handleWheel = e => e.target.blur();

  const deleteItem = (e, idx) => {
    e.preventDefault();
    setMicrosoftlicensingSelections([
      ...microsoftlicensingSelections.slice(0, idx),
      ...microsoftlicensingSelections.slice(idx + 1)
    ]);
  };

  useEffect(() => {
    onChange({
      selections: microsoftlicensingSelections,
      adjustedTotalPrice,
      adjustedTotalCost
    });
  }, [microsoftlicensingSelections, adjustedTotalPrice, adjustedTotalCost]);

  return (
    <div>
      {!!microsoftlicensingSelections.length && (
        <Table>
          <Th>Category</Th>
          <Th>Subscription Line Options</Th>
          <Th>Cost</Th>
          <Th>Price</Th>
          <Th>Qty of Licences</Th>
          <Th>Total Cost</Th>
          <Th>Total Price</Th>
          <Th colSpan={2}>Actions</Th>{' '}
          {/* Add this column for edit/delete buttons */}
          {microsoftlicensingSelections.map(
            (
              {
                category,
                subscriptionLineOption,
                cost,
                price,
                qtyOfLicenses,
                totalCost,
                totalPrice
              },
              idx
            ) => (
              // eslint-disable-next-line react/no-array-index-key
              <Tr key={`${category}-${subscriptionLineOption}-${idx}`}>
                <Td>{category}</Td>
                <Td>{subscriptionLineOption}</Td>
                <Td>${cost}</Td>
                <Td>${price}</Td>
                <Td>{qtyOfLicenses}</Td>
                <Td>{formatCurrency(totalCost)}</Td>
                <Td>{formatCurrency(totalPrice)}</Td>
                <Td>
                  <IconButton
                    icon={Edit}
                    onClick={e =>
                      editItem(e, microsoftlicensingSelections[idx])
                    }
                  />
                </Td>
                <Td>
                  <IconButton icon={Delete} onClick={e => deleteItem(e, idx)} />
                </Td>
              </Tr>
            )
          )}
          <Tr>
            <TdLabel colSpan={5}>Monthly Microsoft Licensing Total</TdLabel>
            <TdImp>{formatCurrency(totals.totalCost)}</TdImp>
            <TdImp>{formatCurrency(totals.totalPrice)}</TdImp>
            <Td />
          </Tr>
        </Table>
      )}
      <Button onClick={showModal}>Add Subscription</Button>
      <Modal
        open={isModalVisible}
        setOpen={onCloseModal}
        customStyles={customStyles}
        header={modalData ? 'Edit Subscription' : 'Add Subscription'}
      >
        <Formik
          initialValues={
            modalData || {
              category: '',
              subscriptionLineOption: '',
              cost: 0,
              price: 0,
              qtyOfLicenses: 0,
              totalCost: 0,
              totalPrice: 0,
              subscriptionPlan: '',
              item: ''
            }
          }
          onSubmit={values => {
            if (modalData) {
              // Update existing row
              const updatedSelections = [...microsoftlicensingSelections];
              updatedSelections[
                microsoftlicensingSelections.indexOf(modalData)
              ] = values;
              setMicrosoftlicensingSelections(updatedSelections);
            } else {
              // Add new row
              addLicencingRow(values);
            }
            if (
              values.qtyOfLicenses !== 0 &&
              (!errors || !errors.qtyOfLicenses)
            ) {
              onCloseModal();
            } else {
              showModal();
            }
          }}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <div>
                <ModalFieldLabel>Category:</ModalFieldLabel>
                <Field
                  as="select"
                  id="category"
                  name="category"
                  className="frg-input-field color-light"
                  onChange={e => {
                    const selectedCategory = e.target.value;
                    setFieldValue('category', selectedCategory);
                    setFieldValue('subscriptionLineOption', ''); // Reset Subscription Line Option
                  }}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </Field>
              </div>

              <div>
                <ModalFieldLabel htmlFor="subscriptionLineOption">
                  Subscription Line Option:
                </ModalFieldLabel>
                <Field
                  as="select"
                  id="subscriptionLineOption"
                  name="subscriptionLineOption"
                  className="frg-input-field color-light"
                  onChange={e => {
                    const selectedOption = e.target.value;
                    setFieldValue('subscriptionLineOption', selectedOption);
                    const selectedItem = data.find(
                      item =>
                        item.Category === values.category &&
                        item['Item Description Line 2'] === selectedOption
                    );
                    if (selectedItem) {
                      setFieldValue('cost', selectedItem.Cost);
                      setFieldValue('price', selectedItem.Price);
                      setFieldValue(
                        'totalCost',
                        round(selectedItem.Cost * values.qtyOfLicenses, 2)
                      );
                      setFieldValue(
                        'totalPrice',
                        round(selectedItem.Price * values.qtyOfLicenses, 2)
                      );
                      setFieldValue(
                        'subscriptionPlan',
                        selectedItem['Subscription Plan']
                      );
                      setFieldValue('item', selectedItem.Item);
                    }
                  }}
                  required
                >
                  <option value="">Select Subscription Line Option</option>
                  {values.category &&
                    getDistinctItemDescriptionLine2(data, values.category).map(
                      itemDesc => (
                        <option key={itemDesc} value={itemDesc}>
                          {itemDesc}
                        </option>
                      )
                    )}
                </Field>
              </div>

              <div>
                <ModalFieldLabel htmlFor="cost">Cost:</ModalFieldLabel>
                <Field
                  type="number"
                  id="cost"
                  name="cost"
                  className="frg-input-field color-light"
                  value={values.cost}
                  disabled
                  onWheel={handleWheel}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <ModalFieldLabel htmlFor="price">Price:</ModalFieldLabel>
                <Field
                  type="number"
                  id="price"
                  name="price"
                  className="frg-input-field color-light"
                  value={values.price}
                  disabled
                  onWheel={handleWheel}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <ModalFieldLabel htmlFor="qtyOfLicenses">
                  Qty of licenses:
                </ModalFieldLabel>
                <Field
                  type="number"
                  id="qtyOfLicenses"
                  name="qtyOfLicenses"
                  className="frg-input-field color-light"
                  onWheel={handleWheel}
                  onChange={e => {
                    const { value } = e.target;
                    setFieldValue('qtyOfLicenses', value);
                    setFieldValue('totalCost', round(values.cost * value, 2));
                    setFieldValue('totalPrice', round(values.price * value, 2));
                    setErrors(prevErrors => ({
                      ...prevErrors,
                      qtyOfLicenses:
                        value > 0
                          ? ''
                          : 'Quantity of Licenses must be greater than 0'
                    }));
                  }}
                  style={{ width: '100%' }}
                  required
                />
              </div>
              {errors && errors.qtyOfLicenses && (
                <div style={{ color: 'red' }}>{errors.qtyOfLicenses}</div>
              )}
              <div>
                <ModalFieldLabel htmlFor="totalCost">
                  Total Cost:
                </ModalFieldLabel>
                <Field
                  type="number"
                  id="totalCost"
                  name="totalCost"
                  className="frg-input-field color-light"
                  value={values.totalCost.toFixed(2)}
                  disabled
                  onWheel={handleWheel}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <ModalFieldLabel htmlFor="totalPrice">
                  Total Price:
                </ModalFieldLabel>
                <Field
                  type="number"
                  id="totalPrice"
                  name="totalPrice"
                  className="frg-input-field color-light"
                  value={values.totalPrice.toFixed(2)}
                  disabled
                  onWheel={handleWheel}
                  style={{ width: '100%' }}
                />
              </div>
              <ModalFooter>
                <Button type="submit">Add</Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

FmMicrosoftLicensing.propTypes = {
  onChange: PropTypes.func.isRequired,
  defaultValues: PropTypes.shape({}).isRequired,
  orderData: PropTypes.shape({}).isRequired
};

export default FmMicrosoftLicensing;
